import React, { useCallback } from "react";
import { connect } from "react-redux";

import { faEye } from "@fortawesome/pro-solid-svg-icons/faEye";
import { faEyeSlash } from "@fortawesome/pro-solid-svg-icons/faEyeSlash";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import Radio from "antd/es/radio";
import { useIntl } from "react-intl";

import MarkerSize from "@mapmycustomers/shared/enum/MarkerSize";
import { Labeled } from "@mapmycustomers/ui";

import RadioGroup from "@app/component/input/RadioGroup";
import { getMapSettings } from "@app/store/iam";
import { updateMetadata } from "@app/store/iam/actions";
import { RootState } from "@app/store/rootReducer";
import useAnalytics from "@app/util/contexts/useAnalytics";
import { getMarkerSizeDisplayName } from "@app/util/ui";

import styles from "./CommonPinSettings.module.scss";

interface Props {
  mapSettings: {
    markerSize: MarkerSize;
    showEntityIcons: boolean;
  };
  onUpdateMetadata?: typeof updateMetadata.request;
}

const CommonPinSettings: React.FC<Props> = ({ mapSettings, onUpdateMetadata }) => {
  const analytics = useAnalytics();
  const intl = useIntl();

  const handleMarkerSizeChange = useCallback(
    (markerSize: MarkerSize) => {
      onUpdateMetadata?.({ markerSize });
      analytics.clicked(["Pin size"], { size: markerSize });
    },
    [analytics, onUpdateMetadata]
  );

  const handleShowEntityIconsChange = useCallback(
    (showEntityIcons: boolean) => {
      onUpdateMetadata?.({ showEntityIcons });
      analytics.clicked(["Show icons"], { show: showEntityIcons });
    },
    [analytics, onUpdateMetadata]
  );

  return (
    <div className={styles.container}>
      <span className={styles.title}>
        {intl.formatMessage({
          id: "map.toolbar.popovers.pinSettings.commonSettings.title",
          defaultMessage: "Style",
          description: "Title of the common pin settings in Pin Settings popover on the map page",
        })}
      </span>
      <div className={styles.controls}>
        <div>
          <Labeled
            label={intl.formatMessage({
              id: "map.toolbar.popovers.pinSettings.common.markerSize.title",
              defaultMessage: "Pin size",
              description: "Pin size radio group title in Pin Settings popover on the map page",
            })}
            labelClassName={styles.label}
          >
            <RadioGroup<MarkerSize>
              onChange={handleMarkerSizeChange}
              value={mapSettings.markerSize}
            >
              {[MarkerSize.SMALL, MarkerSize.LARGE].map((value) => (
                <Radio.Button key={value} value={value}>
                  {getMarkerSizeDisplayName(intl, value)}
                </Radio.Button>
              ))}
            </RadioGroup>
          </Labeled>
        </div>
        <div>
          <Labeled
            label={intl.formatMessage({
              id: "map.toolbar.popovers.pinSettings.common.showEntityIcons.title",
              defaultMessage: "Record icons",
              description:
                "Show entity icons radio group title in Pin Settings popover on the map page",
            })}
            labelClassName={styles.label}
          >
            <RadioGroup<boolean>
              onChange={handleShowEntityIconsChange}
              value={mapSettings.showEntityIcons}
            >
              <Radio.Button value={true}>
                {intl.formatMessage(
                  {
                    id: "map.toolbar.popovers.pinSettings.common.showEntityIcons.yes",
                    defaultMessage: "{icon} Show",
                    description:
                      "Show with icons option name in Pin settings popover on the map page",
                  },
                  {
                    icon: <FontAwesomeIcon icon={faEye} />,
                  }
                )}
              </Radio.Button>
              <Radio.Button value={false}>
                {intl.formatMessage(
                  {
                    id: "map.toolbar.popovers.pinSettings.common.showEntityIcons.no",
                    defaultMessage: "{icon} Hide",
                    description: "Hide icons option name in Pin settings popover on the map page",
                  },
                  {
                    icon: <FontAwesomeIcon icon={faEyeSlash} />,
                  }
                )}
              </Radio.Button>
            </RadioGroup>
          </Labeled>
        </div>
      </div>
    </div>
  );
};

const mapStateToProps = (state: RootState) => ({
  mapSettings: getMapSettings(state),
});

const mapDispatchToProps = {
  onUpdateMetadata: updateMetadata.request,
};

export default connect(mapStateToProps, mapDispatchToProps)(CommonPinSettings);
